import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getUiLang } from '../utils/lang';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { ShoppingCart, ArrowRight, Star, Lock } from 'lucide-react';
import SEO from '../components/SEO';
import RecentlyViewed from '../components/RecentlyViewed';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const { t, i18n } = useTranslation();
  const { addToCart, currency } = useCart();
  const { isAuthenticated } = useAuth();
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [brandsRes, categoriesRes, productsRes] = await Promise.all([
        axios.get(`${API}/brands`),
        axios.get(`${API}/categories`),
        axios.get(`${API}/products?featured=true&limit=6`)
      ]);
      setBrands(brandsRes.data);
      setCategories(categoriesRes.data);
      setFeaturedProducts(productsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTranslation = (obj, field) => {
    return obj.translations?.[getUiLang(i18n)]?.[field] || obj.translations?.['sk']?.[field] || '';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-2xl text-orange-500">{t('common.loading')}</div>
      </div>
    );
  }

  // SEO meta data by language
  const seoData = {
    sk: {
      title: 'Slnečné ochranné fólie na okná',
      description: 'Profesionálne slnečné ochranné fólie pre váš dom a kanceláriu. Zníženie tepla až o 85%, UV ochrana 99%, ochrana súkromia. Armolan, Madico, Solar Gard, Bruxsafol, LLumar.',
      keywords: 'slnečné fólie, ochranné fólie na okná, UV ochrana, tepelná izolácia, súkromie, bezpečnostné fólie'
    },
    cs: {
      title: 'Sluneční ochranné fólie na okna',
      description: 'Profesionální sluneční ochranné fólie pro váš dům a kancelář. Snížení tepla až o 85%, UV ochrana 99%, ochrana soukromí.',
      keywords: 'sluneční fólie, ochranné fólie na okna, UV ochrana, tepelná izolace, soukromí'
    },
    en: {
      title: 'Sun Protection Window Films',
      description: 'Professional solar control window films for your home and office. Heat reduction up to 85%, 99% UV protection, privacy protection. Armolan, Madico, Solar Gard, Bruxsafol, LLumar.',
      keywords: 'sun protection films, window films, UV protection, heat insulation, privacy films'
    },
    de: {
      title: 'Sonnenschutzfolien für Fenster',
      description: 'Professionelle Sonnenschutzfolien für Ihr Zuhause und Büro. Wärmereduzierung bis zu 85%, 99% UV-Schutz, Sichtschutz.',
      keywords: 'Sonnenschutzfolien, Fensterfolien, UV-Schutz, Wärmeisolierung, Sichtschutz'
    },
    ru: {
      title: 'Солнцезащитные пленки для окон',
      description: 'Профессиональные солнцезащитные пленки для вашего дома и офиса. Снижение температуры до 85%, защита от УФ 99%, защита приватности.',
      keywords: 'солнцезащитные пленки, пленки на окна, UV защита, теплоизоляция, приватность'
    }
  };

  const currentSeo = seoData[i18n.language] || seoData.sk;

  return (
    <div className="min-h-screen">
      <SEO 
        title={currentSeo.title}
        description={currentSeo.description}
        keywords={currentSeo.keywords}
        url="/"
      />
      {/* Hero Section */}
      <section className="relative text-white py-32 overflow-hidden" style={{
        backgroundImage: 'url(https://customer-assets.emergentagent.com/job_welcome-app-440/artifacts/qmzcdt7q_sunscape-purelite-20-2024-film-page-3456x1260.webp)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        minHeight: '600px'
      }}>
        {/* Dark overlay for text readability */}
        <div className="absolute inset-0 bg-black/40"></div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-5xl">
            <h1 className="text-5xl md:text-6xl font-bold mb-6 drop-shadow-lg" data-testid="hero-title">
              {t('hero.title')}
            </h1>
            <p 
              className={`text-xl md:text-2xl mb-8 text-white drop-shadow-lg ${i18n.language !== 'ru' ? '' : 'max-w-3xl'}`}
              style={i18n.language !== 'ru' ? { whiteSpace: 'nowrap', maxWidth: 'none' } : {}}
            >
              {t('hero.subtitle')}
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/catalog"
                className="px-8 py-4 bg-white text-orange-500 rounded-lg font-semibold hover:bg-gray-100 transition flex items-center space-x-2 shadow-lg"
                data-testid="hero-cta-shop"
              >
                <span>{t('hero.cta_shop')}</span>
                <ArrowRight className="h-5 w-5" />
              </Link>
              <Link
                to="/services"
                className="px-8 py-4 bg-transparent border-2 border-white text-white rounded-lg font-semibold hover:bg-white/20 transition shadow-lg"
                data-testid="hero-cta-services"
              >
                {t('hero.cta_services')}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Brands Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4" data-testid="brands-title">{t('brands.title')}</h2>
            <p className="text-gray-600 text-lg">{t('brands.subtitle')}</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-8">
            {brands.map((brand) => (
              <Link
                key={brand.id}
                to={`/brands/${brand.slug}`}
                className="bg-white p-6 rounded-lg shadow-md hover:shadow-xl transition flex items-center justify-center"
                data-testid={`brand-card-${brand.slug}`}
              >
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-800 mb-2">
                    {getTranslation(brand, 'name')}
                  </div>
                  <div className="text-sm text-gray-500">
                    {getTranslation(brand, 'description').substring(0, 50)}...
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4" data-testid="categories-title">{t('categories.title')}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {categories
              .filter(cat => !cat.parent_id)  // Only root categories
              .map((category) => (
              <Link
                key={category.id}
                to={`/catalog/category/${category.id}`}
                className="group relative overflow-hidden rounded-xl bg-gradient-to-br from-orange-500 to-yellow-400 p-8 text-white hover:shadow-2xl transition-all duration-300"
                data-testid={`category-card-${category.slug}`}
              >
                <div className="relative z-10">
                  <h3 className="text-2xl font-bold mb-2">{getTranslation(category, 'name')}</h3>
                  <p className="text-white/90">{getTranslation(category, 'description')}</p>
                </div>
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-all"></div>
              </Link>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link
              to="/catalog/categories"
              className="inline-flex items-center gap-2 text-orange-500 hover:text-orange-600 font-medium"
            >
              {t('categories.view_all')}
              <ArrowRight className="h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4" data-testid="featured-title">{t('featured.title')}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {featuredProducts.map((product) => {
              const price = currency === 'EUR' ? product.price_eur : product.price_usd;
              const discountedPrice = price * (1 - product.discount / 100);
              
              return (
                <div
                  key={product.id}
                  className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-xl transition"
                  data-testid={`product-card-${product.sku}`}
                >
                  {product.discount > 0 && (
                    <div className="absolute top-4 right-4 bg-red-500 text-white px-3 py-1 rounded-full text-sm font-bold">
                      -{product.discount}%
                    </div>
                  )}
                  <div className="relative h-64 bg-gray-200">
                    <img
                      src={product.images[0]}
                      alt={getTranslation(product, 'name')}
                      className="w-full h-full object-cover"
                    />
                    {product.is_featured && (
                      <div className="absolute top-4 left-4 bg-orange-500 text-white px-3 py-1 rounded-full text-sm font-bold flex items-center space-x-1">
                        <Star className="h-4 w-4" />
                        <span>{t('featured.badge')}</span>
                      </div>
                    )}
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold mb-2">{getTranslation(product, 'name')}</h3>
                    <p className="text-gray-600 mb-4 line-clamp-2">
                      {getTranslation(product, 'description')}
                    </p>
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        {isAuthenticated ? (
                          product.discount > 0 ? (
                            <div>
                              <div className="text-2xl font-bold text-orange-500">
                                {discountedPrice.toFixed(2)} {currency}
                              </div>
                              <div className="text-sm text-gray-500 line-through">
                                {price.toFixed(2)} {currency}
                              </div>
                            </div>
                          ) : (
                            <div className="text-2xl font-bold text-orange-500">
                              {price.toFixed(2)} {currency}
                            </div>
                          )
                        ) : (
                          <Link
                            to="/login"
                            className="flex items-center space-x-2 text-orange-500 hover:text-orange-600"
                          >
                            <Lock className="h-5 w-5" />
                            <span className="text-sm font-semibold">Prihláste sa pre cenu</span>
                          </Link>
                        )}
                      </div>
                      <div className="text-sm">
                        {product.stock > 1 ? (
                          <span className="text-green-600">{
                            product.unit === 'mb'
                              ? t('product.in_stock_mb', { count: product.stock })
                              : t('product.in_stock_pcs', { count: product.stock })
                          }</span>
                        ) : product.stock === 1 ? (
                          <span className="text-yellow-600">{t('product.check_with_seller_short')}</span>
                        ) : (
                          <span className="text-red-500">{t('product.on_order')}</span>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Link
                        to={`/product/${product.id}`}
                        className="flex-1 px-4 py-2 border border-orange-500 text-orange-500 rounded-lg hover:bg-orange-50 transition text-center"
                        data-testid={`view-product-${product.sku}`}
                      >
                        Zobrazit
                      </Link>
                      {isAuthenticated ? (
                        <button
                          onClick={() => addToCart(product)}
                          disabled={product.stock < 2}
                          className="flex-1 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                          data-testid={`add-to-cart-${product.sku}`}
                        >
                          <ShoppingCart className="h-4 w-4" />
                          <span>{t('product.add_to_cart')}</span>
                        </button>
                      ) : (
                        <Link
                          to="/login"
                          className="flex-1 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition flex items-center justify-center space-x-2"
                        >
                          <Lock className="h-4 w-4" />
                          <span>{t('nav.login')}</span>
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="text-center mt-12">
            <Link
              to="/catalog"
              className="inline-flex items-center space-x-2 px-8 py-4 bg-orange-500 text-white rounded-lg font-semibold hover:bg-orange-600 transition"
              data-testid="view-all-products"
            >
              <span>{t('categories.view_all')}</span>
              <ArrowRight className="h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>
      <RecentlyViewed />
    </div>
  );
};

export default Home;